import React, { useState } from 'react';
import toArray from 'utils/to-array';
import type { AriaAttributes, StyledElement, TestAutomation } from 'contracts';
import { KeyboardKey } from 'contracts';
import withGATracking from 'enhancers/with-ga-tracking';

import Icon, { IconList } from '../icon';
import Html from '../html';

import styles from './checkbox.module.scss';

interface CheckboxElement extends StyledElement, AriaAttributes, TestAutomation {
  checked?: boolean;
  disabled?: boolean;
  error?: boolean;
  variant?: 'standard' | 'outline';
  onChange: (checked: boolean) => void;
  children: React.ReactNode;
  boxClassName?: string | undefined;
}

const Checkbox: React.FunctionComponent<CheckboxElement> = (props) => {
  const {
    testId,
    className,
    style,
    children,
    disabled = false,
    error = false,
    variant = 'standard',
    checked: defaultChecked = false,
    onChange,
    boxClassName,
    ...rest
  } = props;
  const [checked, toggleStatus] = useState<boolean>(defaultChecked || false);
  const ref = React.useRef<HTMLInputElement>(undefined);
  const isOutlineVariant = variant === 'outline';

  const handleOnChange = React.useCallback((): void => {
    if (disabled) return;

    const next = ref.current?.checked ?? false;

    onChange(next);
    toggleStatus(next);
  }, [onChange, disabled]);

  const handleOnKeyDown = React.useCallback(
    (event: React.KeyboardEvent): void => {
      if ([KeyboardKey.Enter, KeyboardKey.Spacebar].includes(event.key as KeyboardKey)) {
        ref.current!.checked = !ref.current!.checked;
        handleOnChange();
      }
    },
    [handleOnChange]
  );

  return (
    <Html.label
      testId={testId}
      onChange={handleOnChange}
      onKeyDown={handleOnKeyDown}
      className={[
        styles.checkbox,
        disabled && styles.disabled,
        isOutlineVariant && styles.outline,
        'inline-block',
        ...toArray(className),
      ]}
      style={style}
      arias={rest}
    >
      <Html.input
        type="checkbox"
        className={['d-none', styles.input]}
        testId={testId && `${testId}-input`}
        defaultChecked={checked}
        disabled={disabled}
        ref={ref}
      />
      <Html.span
        testId={testId && `${testId}-box`}
        className={[
          'rounded-1',
          isOutlineVariant && 'rounded-2',
          styles.box,
          checked && styles.checked,
          disabled && styles.disabled,
          error && styles.error,
          ...toArray(boxClassName),
        ]}
        tabIndex={0}
      >
        <Icon
          name={IconList.checkSolid}
          className={styles.checkmark}
          testId={testId && `${testId}-checkmark`}
          size={20}
        />
      </Html.span>
      <Html.span
        role="checkbox"
        testId={testId && `${testId}-content`}
        aria-checked={checked}
        className={[styles.childrenWrapper, 'mb-0', 'ps-2']}
        typography="body2"
      >
        {children}
      </Html.span>
    </Html.label>
  );
};

export type { CheckboxElement };
export default withGATracking(Checkbox, { action: 'change', handler: 'onChange' });
